import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { components, StylesConfig } from "react-select";
import { AppIcon } from "components/AppIcon";
import { fetchProjectsSelect2, fetchProjectById } from "components/proejctsandquoteapicalls";
import { safeConsole } from "utils/envUtils";
import "components/projectgetcard.css";

const PAGE_SIZE = 20;

export type ProjectGetCardSelection = {
  projectId: string;
  projectNumber: string;
};

export type ProjectSelectOption = {
  value: string;
  label: string;
  projectNumber: string;
};

function mapRowToOption(r: any): ProjectSelectOption | null {
  if (!r || typeof r !== "object") return null;
  const id = String(r.id ?? r._id ?? r.value ?? "").trim();
  if (!id) return null;
  const projectNumber = String(r.project_number ?? r.projectNumber ?? r.number ?? "").trim();
  const title = String(r.title ?? r.name ?? "").trim();
  const label =
    projectNumber && title
      ? `${projectNumber} — ${title}`
      : projectNumber || title || id;
  return { value: id, label, projectNumber: projectNumber || title || id };
}

type Props = {
  token: string;
  signageId: string;
  /** Pre-select when opening e.g. `/createOrder?project_id=...` */
  initialProjectId?: string;
  /** Called when user picks, changes, or clears the project */
  onSelectionChange: (selection: ProjectGetCardSelection | null) => void;
  className?: string;
};

const ACCENT = "#2563eb";

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="bi bi-search" style={{ color: ACCENT, fontSize: "1rem" }} aria-hidden />
  </components.DropdownIndicator>
);

const ClearIndicator = (props: any) => (
  <components.ClearIndicator {...props}>
    <i className="bi bi-x-lg text-secondary" style={{ fontSize: "0.95rem", opacity: 0.75 }} aria-hidden />
  </components.ClearIndicator>
);

const IndicatorSeparator = () => null;

const ProjectGetCard: React.FC<Props> = ({
  token,
  signageId,
  initialProjectId,
  onSelectionChange,
  className,
}) => {
  const [value, setValue] = useState<ProjectSelectOption | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const loadOptions: LoadOptions<ProjectSelectOption, any, { page: number }> = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      if (!token || !signageId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      try {
        const { list, hasMore } = await fetchProjectsSelect2(token, signageId, {
          page,
          limit: PAGE_SIZE,
          search: q,
        });
        const options = list
          .map((row) => mapRowToOption(row))
          .filter(Boolean) as ProjectSelectOption[];
        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (e) {
        safeConsole.warn("[ProjectGetCard] select2 load failed:", e);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, signageId]
  );

  /** Resolve label for `initialProjectId` (URL deep-link or order hydrate). */
  useEffect(() => {
    if (!initialProjectId || !token || !signageId) return;
    let cancelled = false;
    (async () => {
      try {
        const { list } = await fetchProjectsSelect2(token, signageId, {
          page: 1,
          limit: PAGE_SIZE,
          search: initialProjectId,
        });
        const found =
          list.map(mapRowToOption).find((o) => o && o.value === initialProjectId) ||
          list.map(mapRowToOption).find((o) => o && o.projectNumber === initialProjectId);
        if (found && !cancelled) {
          setValue(found);
          onSelectionChangeRef.current?.({
            projectId: found.value,
            projectNumber: found.projectNumber,
          });
          return;
        }
        const detail = await fetchProjectById(token, initialProjectId);
        if (cancelled || !detail?.id) return;
        const opt = mapRowToOption({
          id: detail.id,
          project_number: detail.project_number,
          title: detail.title,
        });
        if (opt) {
          setValue(opt);
          onSelectionChangeRef.current?.({
            projectId: opt.value,
            projectNumber: opt.projectNumber,
          });
        }
      } catch (e) {
        safeConsole.warn("[ProjectGetCard] initial project resolve failed:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialProjectId, token, signageId]);

  const selectStyles = useMemo<StylesConfig<ProjectSelectOption, false>>(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 46,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: state.isFocused ? ACCENT : "#dee2e6",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.18)" : "none",
        backgroundColor: "#fff",
        cursor: "text",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: state.isFocused ? ACCENT : "#ced4da",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        paddingLeft: 16,
        paddingRight: 6,
      }),
      placeholder: (base) => ({
        ...base,
        color: "#868e96",
        fontSize: 14,
      }),
      input: (base) => ({
        ...base,
        fontSize: 14,
        color: "#212529",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#212529",
        fontSize: 14,
        fontWeight: 500,
      }),
      menu: (base) => ({
        ...base,
        /* High enough to sit above sibling cards in the sidebar; menu stays in-DOM (no portal) so it aligns with the control */
        zIndex: 5000,
        borderRadius: 12,
        border: "1px solid #e9ecef",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
        overflow: "hidden",
        marginTop: 6,
      }),
      menuList: (base) => ({
        ...base,
        paddingTop: 6,
        paddingBottom: 6,
        maxHeight: 280,
      }),
      option: (base, state) => ({
        ...base,
        borderRadius: 8,
        marginLeft: 6,
        marginRight: 6,
        padding: "10px 12px",
        fontSize: 14,
        cursor: "pointer",
        backgroundColor: state.isFocused ? "#e7f1ff" : "transparent",
        color: "#212529",
      }),
      indicatorsContainer: (base) => ({
        ...base,
        paddingRight: 10,
        gap: 2,
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: 6,
        cursor: "pointer",
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: 6,
        cursor: "pointer",
      }),
      loadingIndicator: (base) => ({
        ...base,
        color: ACCENT,
      }),
    }),
    []
  );

  return (
    <div className={`card shadow-border-1 mb-3 project-get-card ${className || ""}`}>
      <div className="project-get-card__header">
        <h5 className="project-get-card__title align-items-center d-flex mb-0">
          <AppIcon name="projects" size={22} alt="Project" />
          <span>Project</span>
        </h5>
      </div>
      <div className="project-get-card__body">
        <div className="project-get-card__label">Search by project number</div>
        <AsyncPaginate
          classNamePrefix="project-get-card-select"
          placeholder="Search by project number…"
          isClearable
          debounceTimeout={300}
          value={value}
          loadOptions={loadOptions}
          additional={{ page: 1 }}
          blurInputOnSelect
          menuPlacement="auto"
          components={{ DropdownIndicator, ClearIndicator, IndicatorSeparator }}
          styles={selectStyles}
          onChange={(opt: ProjectSelectOption | null) => {
            setValue(opt);
            if (!opt) {
              onSelectionChange(null);
              return;
            }
            onSelectionChange({
              projectId: opt.value,
              projectNumber: opt.projectNumber,
            });
          }}
        />
      </div>
    </div>
  );
};

export default ProjectGetCard;
